import React, { useState, useEffect } from 'react';
import { BusinessModel, TemplateCategory } from '../types/template';
import { TemplateService } from '../services/templateService';
import './BusinessModelEditor.css';

interface BusinessModelEditorProps {
  isOpen: boolean;
  onClose: () => void;
  businessModel: BusinessModel | null;
  onSave: () => void;
}

const BusinessModelEditor: React.FC<BusinessModelEditorProps> = ({
  isOpen,
  onClose,
  businessModel,
  onSave
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');
  const [transportationMethods, setTransportationMethods] = useState<string[]>([]);
  const [categories, setCategories] = useState<TemplateCategory[]>([]);
  const [newTransportMethod, setNewTransportMethod] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (businessModel) {
        // Edit existing model
        setName(businessModel.name);
        setDescription(businessModel.description);
        setIcon(businessModel.icon);
        setTransportationMethods([...businessModel.transportationMethods]);
        setCategories([...businessModel.categories]);
      } else {
        // Create new model
        setName('');
        setDescription('');
        setIcon('🏭');
        setTransportationMethods([]);
        setCategories([]);
      }
    }
  }, [isOpen, businessModel]);

  const handleAddTransportMethod = () => {
    if (newTransportMethod.trim()) {
      setTransportationMethods([...transportationMethods, newTransportMethod.trim()]);
      setNewTransportMethod('');
    }
  };

  const handleRemoveTransportMethod = (index: number) => {
    setTransportationMethods(transportationMethods.filter((_, i) => i !== index));
  };

  const handleAddCategory = () => {
    const newCategory: TemplateCategory = {
      id: `cat_${Date.now()}`,
      name: 'New Category',
      description: '',
      icon: '📋',
      businessModelId: businessModel?.id || `temp_${Date.now()}`,
      isActive: true
    };
    setCategories([...categories, newCategory]);
  };

  const handleUpdateCategory = (index: number, field: keyof TemplateCategory, value: any) => {
    const updated = [...categories];
    updated[index] = { ...updated[index], [field]: value };
    setCategories(updated);
  };

  const handleRemoveCategory = (index: number) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const { setDoc, updateDoc, doc } = await import('firebase/firestore');
      const { db } = await import('../firebase');

      if (businessModel) {
        // Update existing model
        await updateDoc(doc(db, 'businessModels', businessModel.id), {
          name,
          description,
          icon,
          transportationMethods,
          categories,
          updatedAt: new Date()
        });
      } else {
        // Create new model
        const newModelId = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        await setDoc(doc(db, 'businessModels', newModelId), {
          name,
          description,
          icon,
          transportationMethods,
          categories,
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving business model:', err);
      setError('Failed to save business model');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="business-model-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{businessModel ? '✏️ Edit Business Model' : '➕ Create New Business Model'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="editor-section">
            <label>Business Model Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter business model name"
            />
          </div>

          <div className="editor-section">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description"
              rows={3}
            />
          </div>

          <div className="editor-section">
            <label>Icon (Emoji)</label>
            <input
              type="text"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="Enter emoji icon (e.g., 🏭)"
              maxLength={2}
            />
          </div>

          <div className="editor-section">
            <label>Transportation Methods</label>
            <div className="list-items">
              {transportationMethods.map((method, index) => (
                <div key={index} className="list-item">
                  <span>{method}</span>
                  <button
                    className="remove-btn"
                    onClick={() => handleRemoveTransportMethod(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="add-item-row">
              <input
                type="text"
                value={newTransportMethod}
                onChange={(e) => setNewTransportMethod(e.target.value)}
                placeholder="Add transportation method"
                onKeyPress={(e) => e.key === 'Enter' && handleAddTransportMethod()}
              />
              <button onClick={handleAddTransportMethod} className="add-btn">
                + Add
              </button>
            </div>
          </div>

          <div className="editor-section">
            <label>Categories</label>
            <div className="categories-editor">
              {categories.map((category, index) => (
                <div key={category.id} className="category-editor-item">
                  <div className="category-row">
                    <input
                      type="text"
                      value={category.icon}
                      onChange={(e) => handleUpdateCategory(index, 'icon', e.target.value)}
                      placeholder="Icon"
                      maxLength={2}
                      className="icon-input"
                    />
                    <input
                      type="text"
                      value={category.name}
                      onChange={(e) => handleUpdateCategory(index, 'name', e.target.value)}
                      placeholder="Category name"
                      className="name-input"
                    />
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveCategory(index)}
                    >
                      ×
                    </button>
                  </div>
                  <input
                    type="text"
                    value={category.description}
                    onChange={(e) => handleUpdateCategory(index, 'description', e.target.value)}
                    placeholder="Description (optional)"
                    className="desc-input"
                  />
                </div>
              ))}
            </div>
            <button onClick={handleAddCategory} className="add-category-btn">
              + Add Category
            </button>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="cancel-btn" disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSave} className="save-btn" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessModelEditor;

